import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import moment from 'moment';
import {
  AlertTriangle,
  CheckCircle2,
  Copy,
  Download,
  Edit3,
  ExternalLink,
  File,
  FileText,
  Lock,
  Paperclip,
  Plus,
  Trash2,
} from 'lucide-react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { toast } from 'sonner';
import DeleteVerificationModal from './DeleteVerificationModal';
import { getVerificationPermissions } from './verificationDetailsUtils';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import swal from 'sweetalert';
import './verification-details-modal.css';

const ALLOWED_FILE_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx', '.xlsx', '.xls', '.txt'];
const PREVIEWABLE_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
const PREVIEWABLE_TEXT_EXTENSIONS = ['txt', 'csv', 'json', 'xml'];

const getFileExtension = (fileName = '') => fileName.toLowerCase().split('.').pop() || '';

const getAttachmentKind = (attachment) => {
  const fileName = attachment?.file_name || '';
  const fileType = attachment?.file_type || '';
  const extension = getFileExtension(fileName);

  if (fileType.startsWith('image/') || PREVIEWABLE_IMAGE_EXTENSIONS.includes(extension)) return 'image';
  if (fileType === 'application/pdf' || extension === 'pdf') return 'pdf';
  if (fileType.startsWith('text/') || PREVIEWABLE_TEXT_EXTENSIONS.includes(extension)) return 'text';
  return 'file';
};

const formatCurrency = (amount) => new Intl.NumberFormat('sv-SE', {
  style: 'currency',
  currency: 'SEK',
}).format(Number(amount) || 0);

const formatFileSize = (bytes) => {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return '0 byte';

  const units = ['byte', 'kB', 'MB', 'GB'];
  const unitIndex = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  const size = value / (1024 ** unitIndex);
  return `${new Intl.NumberFormat('sv-SE', { maximumFractionDigits: unitIndex === 0 ? 0 : 1 }).format(size)} ${units[unitIndex]}`;
};

const formatCreatorInfo = (createdByInfo) => {
  if (!createdByInfo) return 'Okänd användare';
  if (createdByInfo.full_name?.trim()) return createdByInfo.full_name.trim();

  const combinedName = `${createdByInfo.first_name || ''} ${createdByInfo.last_name || ''}`.trim();
  return combinedName || createdByInfo.email || 'Okänd användare';
};

const VerificationDetailsModal = ({
  showVerificationDetails,
  setShowVerificationDetails,
  selectedRow,
  selectedFiscalYear,
  onVerificationUpdated,
  viewOnly = false,
}) => {
  const { companyId } = useParams();
  const location = useLocation();
  const history = useHistory();
  const fileInputRef = useRef(null);
  const attachmentRequestIdRef = useRef(0);
  const previewRequestIdRef = useRef(0);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [attachmentError, setAttachmentError] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewText, setPreviewText] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  const permissions = useMemo(
    () => getVerificationPermissions(selectedRow, selectedFiscalYear),
    [selectedRow, selectedFiscalYear]
  );

  const totals = useMemo(() => {
    const result = { debitCents: 0, creditCents: 0 };
    (selectedRow?.transactions || []).forEach((transaction) => {
      const amountCents = Math.round((Number(transaction.amount) || 0) * 100);
      if (transaction.transaction_type === 'debit') result.debitCents += amountCents;
      if (transaction.transaction_type === 'credit') result.creditCents += amountCents;
    });
    return result;
  }, [selectedRow?.transactions]);

  const transactions = selectedRow?.transactions || [];
  const isBalanced = transactions.length > 0 && totals.debitCents === totals.creditCents;
  const canManageAttachments = permissions.canEdit && !viewOnly;

  const closePreview = useCallback(() => {
    previewRequestIdRef.current += 1;
    setPreviewAttachment(null);
    setPreviewUrl(null);
    setPreviewText(null);
    setPreviewLoading(false);
    setPreviewError(null);
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const fetchAttachments = useCallback(async () => {
    if (!selectedRow?.verification_id) return;

    const requestId = ++attachmentRequestIdRef.current;
    setLoadingAttachments(true);
    setAttachmentError(null);

    try {
      const response = await axios.get(
        getApiUrl(`${companyId}/accounting/verifications/${selectedRow.verification_id}/attachments`),
        { withCredentials: true }
      );
      if (attachmentRequestIdRef.current === requestId) {
        setAttachments(response.data.attachments || []);
      }
    } catch (error) {
      if (attachmentRequestIdRef.current === requestId) {
        setAttachments([]);
        setAttachmentError('Det gick inte att hämta bilagorna. Försök igen senare.');
      }
    } finally {
      if (attachmentRequestIdRef.current === requestId) setLoadingAttachments(false);
    }
  }, [companyId, selectedRow?.verification_id]);

  useEffect(() => {
    if (showVerificationDetails && selectedRow?.verification_id) {
      fetchAttachments();
      return;
    }

    attachmentRequestIdRef.current += 1;
    setAttachments([]);
    setAttachmentError(null);
    setLoadingAttachments(false);
    closePreview();
  }, [showVerificationDetails, selectedRow?.verification_id, fetchAttachments, closePreview]);

  const handleCloseModal = () => {
    setShowVerificationDetails(false);
  };

  const handleEdit = () => {
    if (!permissions.canEdit) return;
    setShowVerificationDetails(false);
    history.push({
      pathname: `/${companyId}/edit-verification/${selectedRow.verification_id}`,
      state: { returnTo: location.pathname },
    });
  };

  const handleCopy = () => {
    setShowVerificationDetails(false);
    history.push({
      pathname: `/${companyId}/manual-bookkeeping-entry`,
      state: {
        copyFrom: selectedRow,
        fiscalYear: selectedFiscalYear,
        returnTo: location.pathname,
      },
    });
  };

  const handleDelete = () => {
    if (!permissions.canDelete) return;
    setShowVerificationDetails(false);
    setShowDeleteModal(true);
  };

  const handleFileUpload = async (event) => {
    const input = event.target;
    const files = Array.from(input.files || []);
    if (files.length === 0) return;

    setUploadingFiles(true);
    let uploadedCount = 0;

    try {
      for (const file of files) {
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`Filen "${file.name}" är för stor. Maximal filstorlek är 10 MB.`);
          continue;
        }

        const fileExtension = `.${getFileExtension(file.name)}`;
        if (!ALLOWED_FILE_EXTENSIONS.includes(fileExtension)) {
          toast.error(`Filtypen för "${file.name}" stöds inte.`);
          continue;
        }

        const formData = new FormData();
        formData.append('file', file);

        try {
          const response = await axios.post(
            getApiUrl(`${companyId}/accounting/verifications/${selectedRow.verification_id}/attachments`),
            formData,
            {
              withCredentials: true,
              headers: { 'Content-Type': 'multipart/form-data' },
            }
          );
          if (!response.data.success) throw new Error(response.data.message || 'Upload failed');
          uploadedCount += 1;
        } catch (uploadError) {
          toast.error(`Det gick inte att ladda upp "${file.name}".`);
        }
      }

      if (uploadedCount > 0) {
        await fetchAttachments();
        onVerificationUpdated?.();
        toast.success(uploadedCount === 1 ? 'Bilagan har laddats upp.' : `${uploadedCount} bilagor har laddats upp.`);
      }
    } finally {
      setUploadingFiles(false);
      input.value = '';
    }
  };

  const deleteAttachment = async (attachment) => {
    const fileName = attachment.file_name || 'bilagan';
    const confirmed = await swal({
      title: 'Ta bort bilaga?',
      text: `Bilagan "${fileName}" tas bort permanent.`,
      icon: 'warning',
      buttons: ['Avbryt', 'Ta bort'],
      dangerMode: true,
    });
    if (!confirmed) return;

    try {
      const response = await axios.delete(
        getApiUrl(`${companyId}/accounting/verifications/attachments/${attachment.attachment_id}`),
        { withCredentials: true }
      );
      if (!response.data.success) throw new Error(response.data.message || 'Delete failed');

      setAttachments((current) => current.filter((item) => item.attachment_id !== attachment.attachment_id));
      if (previewAttachment?.attachment_id === attachment.attachment_id) closePreview();
      onVerificationUpdated?.();
      toast.success('Bilagan har tagits bort.');
    } catch (error) {
      toast.error('Det gick inte att ta bort bilagan.');
    }
  };

  const downloadAttachment = async (attachment) => {
    try {
      const response = await axios.get(
        getApiUrl(`${companyId}/attachments/${attachment.attachment_id}`),
        { responseType: 'blob', withCredentials: true }
      );
      const blobUrl = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = attachment.file_name || 'bilaga';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      toast.error('Det gick inte att ladda ner bilagan.');
    }
  };

  const openAttachmentPreview = async (attachment) => {
    const kind = getAttachmentKind(attachment);
    if (kind === 'file') return;

    const requestId = ++previewRequestIdRef.current;
    setPreviewAttachment(attachment);
    setPreviewUrl(null);
    setPreviewText(null);
    setPreviewError(null);
    setPreviewLoading(true);

    try {
      const response = await axios.get(
        getApiUrl(`${companyId}/attachments/${attachment.attachment_id}`),
        {
          responseType: kind === 'text' ? 'text' : 'blob',
          withCredentials: true,
          timeout: 10000,
        }
      );

      if (previewRequestIdRef.current !== requestId) return;
      if (kind === 'text') {
        setPreviewText(String(response.data || ''));
      } else {
        setPreviewUrl(URL.createObjectURL(response.data));
      }
    } catch (error) {
      if (previewRequestIdRef.current === requestId) {
        setPreviewError('Det gick inte att förhandsgranska bilagan.');
      }
    } finally {
      if (previewRequestIdRef.current === requestId) setPreviewLoading(false);
    }
  };

  const verificationLabel = `${selectedRow?.series || ''}${selectedRow?.number || ''}` || '—';
  const createdBy = formatCreatorInfo(selectedRow?.created_by_info);
  const previewKind = getAttachmentKind(previewAttachment);

  return (
    <>
      <Modal
        show={showVerificationDetails}
        onHide={handleCloseModal}
        size="lg"
        centered
        scrollable
        fullscreen="sm-down"
        className="verification-details-modal"
      >
        <Modal.Header closeButton>
          <div className="verification-details__heading">
            <span className="verification-details__eyebrow">Verifikat {verificationLabel}</span>
            <Modal.Title>{selectedRow?.description || 'Utan beskrivning'}</Modal.Title>
          </div>
          <div className="verification-details__header-actions">
            <div className={`verification-details__balance ${transactions.length === 0 ? 'is-empty' : isBalanced ? 'is-balanced' : 'is-unbalanced'}`}>
              {transactions.length === 0 ? null : isBalanced ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
              {transactions.length === 0 ? 'Inga rader' : isBalanced ? 'Balanserar' : 'Balanserar inte'}
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          {!permissions.canEdit && permissions.reason && (
            <div className="verification-details__notice" role="note">
              <Lock size={15} aria-hidden="true" />
              <span>{permissions.reason}</span>
            </div>
          )}

          <section className="verification-details__overview" aria-label="Grundinformation">
            <dl className={`verification-details__metadata ${selectedRow?.notes ? 'has-notes' : ''}`}>
              <div>
                <dt>Verifikationsdatum</dt>
                <dd>{selectedRow?.date ? moment(selectedRow.date).format('D MMMM YYYY') : '—'}</dd>
              </div>
              {selectedRow?.reference && (
                <div>
                  <dt>Referens</dt>
                  <dd>{selectedRow.reference}</dd>
                </div>
              )}
              <div>
                <dt>Skapad av</dt>
                <dd>{createdBy}</dd>
              </div>
              <div>
                <dt>Skapad</dt>
                <dd>{selectedRow?.created_at ? moment(selectedRow.created_at).format('D MMM YYYY HH:mm') : '—'}</dd>
              </div>
            </dl>

            {selectedRow?.notes && (
              <div className="verification-details__overview-notes">
                <h3>Anteckningar</h3>
                <p className="verification-details__notes">{selectedRow.notes}</p>
              </div>
            )}
          </section>

          <section className="verification-details__section" aria-labelledby="verification-transactions-heading">
            <div className="verification-details__section-heading">
              <h3 id="verification-transactions-heading">Konteringsrader</h3>
              <span>{transactions.length} {transactions.length === 1 ? 'rad' : 'rader'}</span>
            </div>

            <div className="verification-details__table-wrap">
              <table className="verification-details__table">
                <thead>
                  <tr>
                    <th>Konto</th>
                    <th>Kontonamn</th>
                    <th className="is-amount">Debet</th>
                    <th className="is-amount">Kredit</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.length > 0 ? transactions.map((transaction, index) => (
                    <tr key={transaction.transaction_id || `${transaction.account_number}-${index}`}>
                      <td className="verification-details__account-number">{transaction.account_number || '—'}</td>
                      <td>{transaction.account_name || '—'}</td>
                      <td className="is-amount">
                        {transaction.transaction_type === 'debit' ? formatCurrency(transaction.amount) : '—'}
                      </td>
                      <td className="is-amount">
                        {transaction.transaction_type === 'credit' ? formatCurrency(transaction.amount) : '—'}
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="4" className="verification-details__empty-table">Inga konteringsrader att visa.</td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="2">Summa</td>
                    <td className="is-amount">{formatCurrency(totals.debitCents / 100)}</td>
                    <td className="is-amount">{formatCurrency(totals.creditCents / 100)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {transactions.length > 0 && !isBalanced && (
              <div className="verification-details__imbalance" role="alert">
                <AlertTriangle size={14} aria-hidden="true" />
                Differens {formatCurrency(Math.abs(totals.debitCents - totals.creditCents) / 100)}
              </div>
            )}
          </section>

          {selectedRow?.invoice?.invoice_id && (
            <section className="verification-details__section" aria-labelledby="verification-invoice-heading">
              <div className="verification-details__section-heading">
                <h3 id="verification-invoice-heading">Kopplad faktura</h3>
              </div>
              <div className="verification-details__related-row">
                <div>
                  <span>Faktura</span>
                  <strong>{selectedRow.invoice.invoice_number || 'Visa faktura'}</strong>
                </div>
                <a
                  href={`/${companyId}/view-invoice/${selectedRow.invoice.invoice_id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="verification-details__text-button"
                >
                  Öppna <ExternalLink size={14} aria-hidden="true" />
                </a>
              </div>
            </section>
          )}

          <section className="verification-details__section" aria-labelledby="verification-attachments-heading">
            <div className="verification-details__section-heading">
              <div>
                <h3 id="verification-attachments-heading">Bilagor</h3>
                <span>{attachments.length} {attachments.length === 1 ? 'bilaga' : 'bilagor'}</span>
              </div>
              {canManageAttachments && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={ALLOWED_FILE_EXTENSIONS.join(',')}
                    onChange={handleFileUpload}
                    hidden
                    disabled={uploadingFiles}
                  />
                  <button
                    type="button"
                    className="verification-details__text-button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingFiles}
                  >
                    {uploadingFiles ? <Spinner animation="border" size="sm" /> : <Plus size={15} aria-hidden="true" />}
                    {uploadingFiles ? 'Laddar upp…' : 'Lägg till bilaga'}
                  </button>
                </>
              )}
            </div>

            {loadingAttachments ? (
              <div className="verification-details__attachments-state">
                <Spinner animation="border" size="sm" /> Hämtar bilagor…
              </div>
            ) : attachmentError ? (
              <div className="verification-details__attachments-error" role="alert">
                {attachmentError}
                <button type="button" onClick={fetchAttachments}>Försök igen</button>
              </div>
            ) : attachments.length === 0 ? (
              <div className="verification-details__attachments-empty">
                <Paperclip size={18} aria-hidden="true" />
                <span>Inga bilagor har lagts till.</span>
              </div>
            ) : (
              <div className="verification-details__attachments-list">
                {attachments.map((attachment) => {
                  const kind = getAttachmentKind(attachment);
                  const fileName = attachment.file_name || 'Namnlös bilaga';
                  return (
                    <div key={attachment.attachment_id} className="verification-details__attachment-row">
                      <div className="verification-details__file-icon" aria-hidden="true">
                        {kind === 'pdf' || kind === 'text' ? <FileText size={17} /> : <File size={17} />}
                      </div>
                      <div className="verification-details__file-info">
                        <strong title={fileName}>{fileName}</strong>
                        <span>{formatFileSize(attachment.file_size)}</span>
                      </div>
                      <div className="verification-details__file-actions">
                        {kind !== 'file' && (
                          <button type="button" onClick={() => openAttachmentPreview(attachment)}>
                            Visa
                          </button>
                        )}
                        <button type="button" onClick={() => downloadAttachment(attachment)}>
                          <Download size={14} aria-hidden="true" />
                          <span className="visually-hidden">Ladda ner {fileName}</span>
                        </button>
                        {canManageAttachments && (
                          <button
                            type="button"
                            className="is-danger"
                            onClick={() => deleteAttachment(attachment)}
                          >
                            <Trash2 size={14} aria-hidden="true" />
                            <span className="visually-hidden">Ta bort {fileName}</span>
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </Modal.Body>

        <Modal.Footer>
          {!viewOnly && (
            <button
              type="button"
              className="verification-details__danger-action"
              onClick={handleDelete}
              disabled={!permissions.canDelete}
            >
              Radera verifikat
            </button>
          )}
          <div className="verification-details__footer-actions">
            {!viewOnly && (
              <>
                <button
                  type="button"
                  className="verification-details__secondary-action"
                  onClick={handleEdit}
                  disabled={!permissions.canEdit}
                  title={!permissions.canEdit ? permissions.reason : 'Redigera verifikat'}
                >
                  <Edit3 size={14} aria-hidden="true" /> Redigera
                </button>
                <button type="button" className="verification-details__secondary-action" onClick={handleCopy}>
                  <Copy size={14} aria-hidden="true" /> Kopiera
                </button>
              </>
            )}
            <button type="button" className="verification-details__secondary-action" onClick={handleCloseModal}>
              Stäng
            </button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal
        show={Boolean(previewAttachment)}
        onHide={closePreview}
        size="lg"
        centered
        scrollable
        className="verification-attachment-preview"
      >
        <Modal.Header closeButton>
          <Modal.Title>{previewAttachment?.file_name || 'Bilaga'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewLoading ? (
            <div className="verification-attachment-preview__state">
              <Spinner animation="border" />
              <span>Hämtar förhandsvisning…</span>
            </div>
          ) : previewError ? (
            <div className="verification-attachment-preview__state is-error">{previewError}</div>
          ) : previewKind === 'image' && previewUrl ? (
            <img src={previewUrl} alt={previewAttachment?.file_name || 'Bilaga'} />
          ) : previewKind === 'pdf' && previewUrl ? (
            <iframe src={`${previewUrl}#toolbar=0&view=FitH`} title={previewAttachment?.file_name || 'PDF-bilaga'} />
          ) : previewKind === 'text' ? (
            <pre>
              {previewText
                ? `${previewText.substring(0, 200000)}${previewText.length > 200000 ? '\n\n… Förhandsvisningen har förkortats.' : ''}`
                : 'Filen är tom.'}
            </pre>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="verification-details__secondary-action" onClick={closePreview}>Stäng</button>
          <button
            type="button"
            className="verification-details__primary-action"
            onClick={() => downloadAttachment(previewAttachment)}
          >
            <Download size={14} aria-hidden="true" /> Ladda ner
          </button>
        </Modal.Footer>
      </Modal>

      {selectedRow && (
        <DeleteVerificationModal
          show={showDeleteModal}
          handleClose={() => setShowDeleteModal(false)}
          verificationData={selectedRow}
          companyId={companyId}
          onVerificationDeleted={onVerificationUpdated}
        />
      )}
    </>
  );
};

export default VerificationDetailsModal;
